// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx, ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {Directions} from 'common/components/drop-menu/types';
import {Placement} from 'common/components/tooltip/types';
import {BoardsDashboardSelector} from 'common/widgets/dashboard-selector/presets';

import {SaveModes} from '../types';
import css from './index.inline.styl';

interface BoardsSaveButtonParams {
  elref: string;
  selectedDashboardId: number;
  userId: number;
  canSaveExisting: boolean;
  canSaveAsNew: boolean;
  tooltip?: string;
  alignRight?: boolean;
  searchPlaceholder: string;
  isSaving?: boolean;
  hasUnsavedChanges: boolean;
  hasExistingReport: boolean;
  showSaveSpinner: boolean;
  onMenuChange: ({state}: {state: boolean}) => void;
  onSave: ({dashboardId, saveMode}) => void;
}

interface BoardsSaveButtonJsxProps {
  params?: BoardsSaveButtonParams;
  props?: {
    dashboardController: DashboardSelectorController;
    recentDashboardIds?: number[];
  };
}

interface BoardsSaveButtonState {
  isOpen: boolean;
  recentDashboardIds: number[];
  pendingSaveMode?: SaveModes;
}

export class BoardsSaveButton extends ParamComponent<
  BoardsSaveButtonParams,
  BoardsSaveButtonState,
  IronContextRegistry
> {
  private declare _dashboardController: DashboardSelectorController;

  get config() {
    return {
      defaultState: {
        isOpen: false,
        recentDashboardIds: [],
        pendingSaveMode: null,
      },
      params: {
        elref: String,
        selectedDashboardId: Number,
        userId: Number,
        canSaveExisting: Boolean,
        canSaveAsNew: Boolean,
        tooltip: String,
        alignRight: Boolean,
        searchPlaceholder: String,
        isSaving: Boolean,
        hasUnsavedChanges: Boolean,
        hasExistingReport: Boolean,
        showSaveSpinner: Boolean,
        onMenuChange: Function,
        onSave: Function,
      },
      contexts: [`currentProject` as const, `i18n` as const],
      useShadowDom: true,
      css,
      template: this.render,
    };
  }

  handleSaveButtonClicked = () => {
    if (this.params.selectedDashboardId) {
      this.params.onSave({dashboardId: this.params.selectedDashboardId, saveMode: SaveModes.Save});
    } else {
      this.update({isOpen: true, pendingSaveMode: SaveModes.Save});
    }
  };

  handleSaveAsNewButtonClicked = () => {
    if (this.params.canSaveExisting && this.params.selectedDashboardId) {
      this.params.onSave({dashboardId: this.params.selectedDashboardId, saveMode: SaveModes.SaveNew});
    } else {
      this.update({isOpen: true, pendingSaveMode: SaveModes.SaveNew});
    }
  };

  handleDashboardSelected = (ev) => {
    const {pendingSaveMode} = this.state;
    this.update({isOpen: false, pendingSaveMode: null});
    this.params.onSave({dashboardId: ev.detail.id, saveMode: pendingSaveMode});
    ev.preventDefault();
  };

  handleMenuChanged = (ev) => {
    this.update({isOpen: ev.detail.state === `open`});
    this.params.onMenuChange({state: ev.detail.state});
  };

  set dashboardController(controller: DashboardSelectorController) {
    controller.getDashboards();
    this._dashboardController = controller;
    this.update();
  }
  get dashboardController() {
    return this._dashboardController;
  }

  set recentDashboardIds(value) {
    this.update({recentDashboardIds: value});
  }

  get currentProject() {
    return this.getContext(`currentProject`).get().value;
  }

  icon() {
    if (this.params.showSaveSpinner) {
      return `spinner`;
    } else if (!this.params.hasUnsavedChanges) {
      return `checkmark`;
    }
    return ``;
  }

  showSaveAsNew() {
    return this.params.hasExistingReport;
  }

  isSaveEnabled() {
    return this.params.canSaveExisting && this.params.hasUnsavedChanges && !this.params.isSaving;
  }

  isSaveAsNewEnabled() {
    return this.params.canSaveAsNew && !this.params.isSaving;
  }

  render(this: BoardsSaveButton) {
    const {t} = this.getContext(`i18n`);
    return (
      <div class={{'mp-boards-save-button': true}}>
        {this.showSaveAsNew() && (
          <ButtonEl
            sel='.save-as-new'
            attrs={{
              theme: this.params.canSaveExisting ? ButtonTheme.Secondary : ButtonTheme.Primary,
              disabled: !this.isSaveAsNewEnabled(),
              tooltip: this.isSaveAsNewEnabled() && {text: t(`reports.actions.saving.tooltips.save-new`)},
            }}
            on={{click: this.handleSaveAsNewButtonClicked}}
          >
            {t(`reports.actions.saving.save-new`)}
          </ButtonEl>
        )}
        <ButtonEl
          sel='.save'
          attrs={{
            icon: this.icon(),
            theme: this.params.canSaveExisting ? ButtonTheme.Primary : ButtonTheme.Secondary,
            pressed: this.state.isOpen,
            disabled: !this.isSaveEnabled() || this.params.showSaveSpinner,
            tooltip: this.params.tooltip ? {placement: Placement.Top, text: this.params.tooltip} : null,
          }}
          on={{click: this.handleSaveButtonClicked}}
          class={{save: true}}
        >
          {this.params.hasUnsavedChanges || this.params.isSaving
            ? t(`reports.actions.saving.save`)
            : t(`reports.actions.saving.saved`)}
        </ButtonEl>

        {this.state.isOpen && (
          <BoardsDashboardSelector
            recentDashboardIds={this.state.recentDashboardIds}
            placeholder={this.params.searchPlaceholder}
            open={this.state.isOpen}
            direction={Directions.Down}
            dashboardController={this.dashboardController}
            handleDashboardSelected={this.handleDashboardSelected}
            handleMenuChanged={this.handleMenuChanged}
            alignRight={this.params.alignRight}
          />
        )}
      </div>
    );
  }
}

export const BoardsSaveButtonEl = defineAndMakeJsxFactory<BoardsSaveButtonJsxProps>(
  `mp-boards-save-button`,
  BoardsSaveButton,
);
export const BoardsSaveButtonReact = panel2react<BoardsSaveButtonJsxProps>(`mp-boards-save-button`, BoardsSaveButton);
